import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "./auth";
import { MobileBlockScreen, isMobileDevice } from "./MobileBlockScreen";
import { LoginScreen } from "./coaching/LoginScreen";
import { DataNoticeScreen } from "./auth/DataNoticeScreen";
import { SignupScreen } from "./auth/SignupScreen";
import { VerifyEmailScreen } from "./auth/VerifyEmailScreen";
import { MeProvider } from "./users/useMe";
import { CanvasHome } from "./canvas/CanvasHome";
import { CanvasFlow } from "./canvas/CanvasFlow";
import { DocUploadStep } from "./canvas/doc-upload/DocUploadStep";
import { OutputsScreen } from "./canvas/OutputsScreen";
import { SessionReview } from "./canvas/SessionReview";
import { VersionSnapshot } from "./canvas/VersionSnapshot";
import { ReviewWizard } from "./canvas/ReviewWizard";
import { ReviewDeltaScreen } from "./canvas/ReviewDelta";
import { CoachPanel } from "./canvas/CoachPanel";
import { getReview, startReview, jumpTo } from "./canvas/api";
import { useToast } from "./ui/Toast";
import { LandingScreen } from "./landing/LandingScreen";
import { UpgradeModal } from "./users/UpgradeModal";
import { getInvitePreview } from "./users/me-api";
import { ScoringModelProvider } from "./canvas/scoringModel";

type View =
  | { kind: "landing" }
  | { kind: "login" }
  | { kind: "home" }
  | { kind: "data-notice" }
  | { kind: "signup"; initialInviteCode?: string; initialFullName?: string; initialEmail?: string }
  | { kind: "canvas"; id: string }
  | { kind: "doc-upload"; id: string; workflowName?: string; midBuild?: boolean }
  | { kind: "review"; id: string }
  | { kind: "outputs"; id: string }
  | { kind: "review-wizard"; id: string }
  | { kind: "review-delta"; id: string }
  | {
      kind: "version-snapshot";
      id: string;
      workflowName: string;
      version: number;
      createdAt: number;
      grade: string;
      composite: number;
    }
  | {
      kind: "coach";
      id: string;
      workflowName: string;
      version: number;
    };

export default function App() {
  const { user, loading } = useAuth();
  const [view, setView] = useState<View>({ kind: "landing" });
  const [showGoProModal, setShowGoProModal] = useState(false);
  const toast = useToast();

  // ?canvas=<id> deep link — on first authenticated mount, route to outputs.
  // Also handles ?intent=free — routes unauthenticated users to data-notice.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const intent = params.get("intent");
    if (intent === "free" && !user) {
      setView({ kind: "data-notice" });
      params.delete("intent");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    }

    // ?invite=<code> — from an invite email. Goes straight to signup with the
    // code pre-filled (skips the data-notice free-tier consent step, since an
    // invited user isn't on the anonymized-data free tier). Name + email are
    // fetched right after and patched in once known, so navigation doesn't wait
    // on the network — the code alone is enough to render the locked field.
    const invite = params.get("invite");
    if (invite && !user) {
      setView({ kind: "signup", initialInviteCode: invite });
      params.delete("invite");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
      getInvitePreview(invite)
        .then((preview) => {
          if (!preview) return;
          setView((v) =>
            v.kind === "signup" && v.initialInviteCode === invite
              ? { ...v, initialFullName: preview.name, initialEmail: preview.email }
              : v,
          );
        })
        .catch(() => undefined);
    }

    if (!user) return;

    // Note: ?card=success|cancel (Stripe card setup) is handled in CanvasHome,
    // which restores the stashed canvas draft and auto-creates.

    const canvasId = params.get("canvas");
    if (canvasId) {
      setView({ kind: "outputs", id: canvasId });
      params.delete("canvas");
      const qs = params.toString();
      const next = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", next);
    }
  }, [user]);

  // Phones get a "use a computer" notice instead of the app (Pascal, Aug 20).
  if (isMobileDevice()) return <MobileBlockScreen />;

  if (loading) return <div className="app-loading">Loading…</div>;

  // Pre-auth views — public.
  if (!user) {
    if (view.kind === "data-notice") {
      return (
        <>
          <DataNoticeScreen
            onStartFree={() => {
              window.localStorage.setItem("og.dataNoticeAccepted", "true");
              setView({ kind: "signup" });
            }}
            onGoPro={() => setShowGoProModal(true)}
          />
          <UpgradeModal
            open={showGoProModal}
            feature="individual-plan"
            onClose={() => setShowGoProModal(false)}
            onSignUp={() => {
              // Pro path skips the free data-contribution consent.
              setShowGoProModal(false);
              setView({ kind: "signup" });
            }}
          />
        </>
      );
    }
    if (view.kind === "signup") {
      const inviteGateEnabled = (import.meta.env.VITE_INVITE_GATE_ENABLED ?? "true") === "true";
      return (
        <SignupScreen
          inviteGateEnabled={inviteGateEnabled}
          initialInviteCode={view.initialInviteCode}
          initialFullName={view.initialFullName}
          initialEmail={view.initialEmail}
          onSignedUp={() => setView({ kind: "home" })}
          onBackToLogin={() => setView({ kind: "login" })}
        />
      );
    }
    if (view.kind === "login") {
      return <LoginScreen onGoSignup={() => setView({ kind: "data-notice" })} />;
    }
    // Default anonymous view = landing.
    return (
      <LandingScreen
        onStartFree={() => setView({ kind: "data-notice" })}
        onSignIn={() => setView({ kind: "login" })}
        onGoPro={() => setView({ kind: "signup" })}
      />
    );
  }

  // Email-verification gate — applies to EVERY authenticated session, so a user
  // cannot reach the app with an unverified email whether they just signed up or
  // signed in directly. Closes the "direct sign-in without completing signup" gap.
  if (!user.emailVerified) {
    return <VerifyEmailScreen email={user.email ?? ""} />;
  }

  const goHome = () => setView({ kind: "home" });

  let content: ReactNode;
  if (view.kind === "doc-upload") {
    content = (
      <DocUploadStep
        sessionId={view.id}
        workflowName={view.workflowName}
        midBuild={view.midBuild}
        onContinue={() => setView({ kind: "canvas", id: view.id })}
        onSkip={() => setView({ kind: "canvas", id: view.id })}
      />
    );
  } else if (view.kind === "canvas") {
    content = (
      <CanvasFlow
        sessionId={view.id}
        onHome={goHome}
        onPreview={() => setView({ kind: "review", id: view.id })}
        onComplete={() => setView({ kind: "outputs", id: view.id })}
        onUploadDoc={() => setView({ kind: "doc-upload", id: view.id, midBuild: true })}
      />
    );
  } else if (view.kind === "review") {
    content = (
      <SessionReview
        sessionId={view.id}
        onHome={goHome}
        onResume={() => setView({ kind: "canvas", id: view.id })}
        onOutputs={() => setView({ kind: "outputs", id: view.id })}
        onEditField={async (phase, fieldIndex) => {
          try {
            await jumpTo(view.id, { phase, fieldIndex });
            setView({ kind: "canvas", id: view.id });
          } catch (e) {
            toast.error((e as Error).message);
          }
        }}
        onReviewField={async (layer) => {
          // Completed canvas: edits must go through the versioned Phase-10
          // review, not mutate the live doc. Open the wizard on this layer.
          try {
            const existing = await getReview(view.id);
            if (!existing) await startReview(view.id, layer);
            setView({ kind: "review-wizard", id: view.id });
          } catch (e) {
            toast.error((e as Error).message);
          }
        }}
      />
    );
  } else if (view.kind === "outputs") {
    content = (
      <OutputsScreen
        sessionId={view.id}
        onHome={goHome}
        onBack={() => setView({ kind: "review", id: view.id })}
        onOpenCoach={() =>
          setView({ kind: "coach", id: view.id, workflowName: "", version: 0 })
        }
      />
    );
  } else if (view.kind === "review-wizard") {
    content = (
      <ReviewWizard
        sessionId={view.id}
        onCancel={goHome}
        onReadyToFinalize={() =>
          setView({ kind: "review-delta", id: view.id })
        }
      />
    );
  } else if (view.kind === "review-delta") {
    content = (
      <ReviewDeltaScreen
        sessionId={view.id}
        onCancel={goHome}
        onFinalized={() => setView({ kind: "outputs", id: view.id })}
      />
    );
  } else if (view.kind === "version-snapshot") {
    content = (
      <VersionSnapshot
        sessionId={view.id}
        workflowName={view.workflowName}
        version={view.version}
        createdAt={view.createdAt}
        grade={view.grade}
        composite={view.composite}
        onHome={goHome}
      />
    );
  } else if (view.kind === "coach") {
    content = (
      <CoachPanel
        sessionId={view.id}
        workflowName={view.workflowName}
        currentVersion={view.version}
        onHome={goHome}
      />
    );
  } else {
    content = (
      <CanvasHome
        onOpen={(id) => setView({ kind: "canvas", id })}
        onOpenWithUpload={(id, workflowName) =>
          setView({ kind: "doc-upload", id, workflowName })
        }
        onReview={(id) => setView({ kind: "review", id })}
        onOpenVersion={(params) => setView({ kind: "version-snapshot", ...params })}
        onOpenCoach={(id, workflowName, version) =>
          setView({ kind: "coach", id, workflowName, version })
        }
        onStartReview={async (id) => {
          try {
            const existing = await getReview(id);
            if (!existing) await startReview(id);
            setView({ kind: "review-wizard", id });
          } catch (e) {
            toast.error((e as Error).message);
          }
        }}
      />
    );
  }

  return (
    <MeProvider>
      <ScoringModelProvider>{content}</ScoringModelProvider>
    </MeProvider>
  );
}
